import AxiosStatic from 'axios';

import {
    TaxInterviewData,
    TaxProfile,
    AccountStatusResponse,
    RetakeAvailableResponse,
    TaxDocumentsResponse,
    PayoutFormValues
} from '../types/payout.types';
import { API_ENDPOINTS } from '../constants/api';
import axios from './client';
import { EmailExistedResponse } from '../types/account.type';

export interface PayoutResponse {
    Status: string;
    data: {
        payoutRes: PayoutFormValues;
        taxinfoRes: boolean;
    };
}

export interface TaxApiResponse<T> {
    Status: string;
    data: T;
    status?: string;
}

export class PayoutService {
    static async fetchConsentUrl(preload: {
        locale: string;
        prizeId: string;
        dob?: string;
    }): Promise<TaxInterviewData> {
        try {
            const response = await axios.post<TaxApiResponse<TaxInterviewData>>(
                API_ENDPOINTS.GET_CONSENT_INTERVIEW,
                preload
            );
            if (response.data.status === 'OK') {
                return response.data.data;
            } else {
                throw new Error('Failed to fetch tax interview url:');
            }
        } catch (error) {
            if (AxiosStatic.isAxiosError(error)) {
                throw new Error(`request failed: ${error.message}`);
            }
            throw new Error('Unknown error occurred');
        }
    }

    static async fetchTaxProfile(): Promise<TaxProfile> {
        try {
            const response = await axios.get<TaxApiResponse<TaxProfile>>(API_ENDPOINTS.GET_PROFILE);

            if (response.data.Status === 'OK') {
                return response.data.data;
            } else {
                throw new Error('Failed to fetch tax profile:');
            }
        } catch (error) {
            if (AxiosStatic.isAxiosError(error)) {
                throw new Error(`request failed: ${error.message}`);
            }
            throw new Error('Unknown error occurred');
        }
    }

    static async fetchInterview(params: {
        locale: string;
        dob?: string;
    }): Promise<TaxInterviewData> {
        try {
            const response = await axios.post<TaxApiResponse<TaxInterviewData>>(
                API_ENDPOINTS.GET_INTERVIEW,
                params
            );
            if (response.data.status === 'OK') {
                return response.data.data;
            } else {
                throw new Error('Failed to fetch tax interview url:');
            }
        } catch (error) {
            if (AxiosStatic.isAxiosError(error)) {
                throw new Error(`request failed: ${error.message}`);
            }
            throw new Error('Unknown error occurred');
        }
    }

    static async updateTaxProfile(profileData: Partial<TaxProfile>): Promise<TaxProfile> {
        try {
            const response = await axios.put<TaxApiResponse<TaxProfile>>(
                API_ENDPOINTS.UPDATE_PROFILE,
                profileData
            );
            if (response.data.status === 'OK') {
                return response.data.data;
            } else {
                throw new Error('Failed to update tax profile:');
            }
        } catch (error) {
            if (AxiosStatic.isAxiosError(error)) {
                throw new Error(`request failed: ${error.message}`);
            }
            throw new Error('Unknown error occurred');
        }
    }

    static async fetchAccountStatus(): Promise<AccountStatusResponse> {
        try {
            const response = await axios.get<AccountStatusResponse>(
                API_ENDPOINTS.GET_ACCOUNT_STATUS
            );
            if (response.data.Status === 'OK') {
                return response.data;
            } else {
                throw new Error('Failed to fetch account status:');
            }
        } catch (error) {
            if (AxiosStatic.isAxiosError(error)) {
                throw new Error(`request failed: ${error.message}`);
            }
            throw new Error('Unknown error occurred');
        }
    }

    static async fetchRetakeAvailable(): Promise<boolean> {
        try {
            const response = await axios.get<RetakeAvailableResponse>(
                API_ENDPOINTS.GET_RETAKE_AVAILABLE
            );
            if (response.data.Status === 'OK') {
                return response.data.data.available;
            } else {
                throw new Error('Failed to fetch retake available status:');
            }
        } catch (error) {
            if (AxiosStatic.isAxiosError(error)) {
                throw new Error(`request failed: ${error.message}`);
            }
            throw new Error('Unknown error occurred');
        }
    }

    static async fetchTaxDocuments(): Promise<TaxDocumentsResponse> {
        try {
            const response = await axios.get<TaxDocumentsResponse>(API_ENDPOINTS.GET_TAX_DOCUMENTS);
            if (response.data.Status === 'OK') {
                return response.data;
            } else {
                throw new Error('Failed to fetch tax documents:');
            }
        } catch (error) {
            if (AxiosStatic.isAxiosError(error)) {
                throw new Error(`request failed: ${error.message}`);
            }
            throw new Error('Unknown error occurred');
        }
    }

    static traceTakeInterview(): void {
        axios.post(API_ENDPOINTS.TRACE_TAKE_INTERVIEW).catch(() => {
            console.error('Failed to track tax interview');
        });
    }

    static async updatePayout(payoutData: PayoutFormValues): Promise<PayoutFormValues> {
        try {
            const response = await axios.post<TaxApiResponse<PayoutFormValues>>(
                API_ENDPOINTS.UPDATE_PAYOUT,
                payoutData
            );
            if (response.data.status === 'OK') {
                return response.data.data;
            } else {
                throw new Error('Failed to update payout:');
            }
        } catch (error) {
            if (AxiosStatic.isAxiosError(error)) {
                throw new Error(`request failed: ${error.message}`);
            }
            throw new Error('Unknown error occurred');
        }
    }

    static async fetchPayout(): Promise<PayoutFormValues> {
        try {
            const response = await axios.get<PayoutResponse>(API_ENDPOINTS.GET_PAYOUT);
            if (response.data.Status === 'OK') {
                return response.data.data.payoutRes;
            } else {
                throw new Error('Failed to fetch payout:');
            }
        } catch (error) {
            if (AxiosStatic.isAxiosError(error)) {
                throw new Error(`request failed: ${error.message}`);
            }
            throw new Error('Unknown error occurred');
        }
    }

    static async checkEmailExisted(email: string): Promise<EmailExistedResponse> {
        try {
            const response = await axios.post<EmailExistedResponse>(
                API_ENDPOINTS.CHECK_EMAIL_EXISTED,
                { email }
            );
            if (response.data.status === 'OK') {
                return response.data;
            } else {
                throw new Error('Failed to check email existence');
            }
        } catch (error) {
            if (AxiosStatic.isAxiosError(error)) {
                throw new Error(`request failed: ${error.message}`);
            }
            throw new Error('Unknown error occurred');
        }
    }
}
